import { useState } from 'react';
import { useAppStore, useProductStore, useReceiptStore } from 'store';
import { SCREEN_TYPES } from 'utils/constants';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { handleProblemResolve } from 'utils/handleProblem';
import { handleScanItemPageOpened } from '../utils';
import { MoveToPRScreen } from './MoveToPRScreen';
import { CallbackScreen } from './CallbackScreen';

export default function ConfirmItem() {
  const [loading, setLoading] = useState(false);
  const { setCurrentScreen } = useAppStore();
  const { currentProduct: productLine, setCurrentProduct } = useProductStore();
  const { receiptData } = useReceiptStore();

  const onConfirm = () => setCurrentScreen(SCREEN_TYPES.GRADE_ITEM);
  const isUnexpected = productLine?.product_id === 'Unknown' || productLine?.expected_ind === 'n';
  const isCallback =
    productLine?.exception_process === 'callback' || productLine?.disposition_stage === 'callback';

  const handleProblem = async (): Promise<void> => {
    setLoading(true);
    const result = await handleProblemResolve(SCREEN_TYPES.CONFIRM_ITEM);
    if (!result.success) {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleScanItemPageOpened();
    setCurrentProduct(null);
  };

  if (isCallback) {
    return (
      <CallbackScreen
        receiptData={receiptData}
        productLine={productLine}
        loading={loading}
        handleCancel={handleCancel}
        onConfirm={onConfirm}
        handleProblem={handleProblem}
      />
    );
  }

  if (isUnexpected) {
    return (
      <MoveToPRScreen
        trackingId={receiptData?.tracking_id || receiptData?.receipt?.tracking_id || ''}
        responseState={receiptData?.responseState || 'unknown'}
        responseMsg={receiptData?.responseMsg || 'No response message'}
        title="Confirm Item"
        handleCancel={handleCancel}
        children={
          <Button
            type="button"
            className="h-14 w-full rounded-2xl bg-white/20 border-2 border-white text-lg font-bold text-white shadow-lg hover:bg-white/30"
            onClick={handleProblem}
            disabled={loading}
          >
            {loading ? 'Reporting...' : 'Problem'}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      <div className="flex-1">
        <CardTitle className="mb-3 text-center text-lg font-extrabold tracking-tight text-slate-900">
          Confirm Item
        </CardTitle>

        {productLine?.style_image_url && (
          <div className="mb-3 flex justify-center rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-3">
            <img
              src={productLine.style_image_url}
              alt="Product"
              className="max-h-56 max-w-full rounded-lg object-contain"
            />
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm space-y-2">
          <InfoRow
            label="Tracking"
            value={receiptData?.tracking_id || receiptData?.receipt?.tracking_id}
          />
          <InfoRow label="Article" value={isUnexpected ? 'Unknown' : productLine?.product_id} />
          <InfoRow label="Color" value={isUnexpected ? 'N/A' : productLine?.style_color} />
          <InfoRow label="Size" value={isUnexpected ? 'N/A' : productLine?.product_size} />
        </div>
      </div>

      <div className="sticky bottom-0 bg-white pt-3 pb-1 buttons-container">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={loading}
          className="h-11 flex-1 rounded-xl border-2 border-red-600 text-sm font-semibold text-red-600 hover:bg-red-50"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className="h-11 flex-1 rounded-xl text-sm font-semibold shadow-sm"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

const InfoRow = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className="flex items-baseline gap-2">
    <span className="w-16 shrink-0 text-xs font-semibold text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-900 break-all">{value ?? 'N/A'}</span>
  </div>
);
